/**
 * Route mapper for pageview tracking.
 */

import { matchPath } from 'react-router-dom';
import { ROUTE_MAPPINGS } from './constants.js';
import type {
  CanonicalPageviewPath,
  PageviewContext,
  PageviewProperties,
  RouteMapping,
  ScreenPath
} from './types.js';

// ROUTE_MAPPINGS is `as const` so types.ts can derive the canonical-path union
// from it. Iterating it needs the widened view: `requiresFlowId` is absent from
// the literal type of every entry that omits it, so the union has no such
// property to read. `satisfies` in constants.ts already proved this is sound.
const ROUTE_MAPPING_ENTRIES: Record<string, RouteMapping> = ROUTE_MAPPINGS;

// React Router always hands back a rooted pathname, but types it as a bare
// `string`. A predicate (not a cast) is what lets the fallback below satisfy
// ScreenPath without weakening the contract for real callers.
const isScreenPath = (value: string): value is ScreenPath => value.startsWith('/');

/**
 * Map a route path to a pageview configuration.
 *
 * @param pathname - The current pathname from React Router
 * @returns Pageview properties to track
 */
export function mapRouteToPageview(pathname: string): PageviewProperties {
  // Try to match against known route patterns
  for (const [pattern, mapping] of Object.entries(ROUTE_MAPPING_ENTRIES)) {
    const match = matchPath(pattern, pathname);
    if (match) {
      return buildPageviewProperties(mapping, match.params);
    }
  }

  // Fallback: use the pathname as-is so unmapped routes still group by path
  return {
    $pathname: isScreenPath(pathname) ? pathname : `/${pathname}`
  };
}

/**
 * Build pageview properties from a route mapping and params.
 *
 * @param mapping - The route mapping configuration
 * @param params - Route parameters
 * @returns Pageview properties
 */
function buildPageviewProperties(
  mapping: RouteMapping,
  params: Readonly<Record<string, string | undefined>>
): PageviewProperties {
  // Canonical grouped path goes in $pathname; $current_url is minted at
  // capture time by trackPageview (PostHog requires it to be absolute).
  const properties: PageviewProperties = {
    $pathname: mapping.path
  };

  if (mapping.category) {
    properties.category = mapping.category;
  }

  if (mapping.section) {
    properties.section = mapping.section;
  }

  // Add route parameters as context
  if (params.roomId) {
    properties.roomId = params.roomId;
  }

  if (params.agentId) {
    properties.agentId = params.agentId;
  }

  if (params.organizationId) {
    properties.organizationId = params.organizationId;
  }

  return properties;
}

/**
 * Build pageview properties for a modal/dialog.
 *
 * @param path - The virtual path for the modal
 * @param category - Category for the modal
 * @param section - Section within the category
 * @param additionalProps - Additional properties to include
 * @returns Pageview properties
 */
export function buildModalPageview(
  path: CanonicalPageviewPath,
  category: string,
  section?: string,
  additionalProps?: PageviewContext
): PageviewProperties {
  const properties: PageviewProperties = {
    $pathname: path,
    category
  };

  if (section) {
    properties.section = section;
  }

  if (additionalProps) {
    Object.assign(properties, additionalProps);
  }

  return properties;
}

/**
 * Generate a unique flow ID for multi-step workflows.
 *
 * @returns A unique flow ID
 */
export function generateFlowId(): string {
  return `flow_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Check if a route requires a flow ID.
 *
 * @param pathname - The current pathname
 * @returns Whether the route requires a flow ID
 */
export function requiresFlowId(pathname: string): boolean {
  for (const [pattern, mapping] of Object.entries(ROUTE_MAPPING_ENTRIES)) {
    const match = matchPath(pattern, pathname);
    if (match && mapping.requiresFlowId === true) {
      return true;
    }
  }
  return false;
}
